import { useEffect, useState } from 'react';

import { Box, Container } from '@mui/material';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { requestAll } from '../../../apis/config';
import { serverApis } from '../../../apis/serverApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import Text from '../../../components/elements/text/Text';
import Editor from '../../../components/page-sections/editor/Editor';
import JobStatus from '../../../components/page-sections/server-jobs/JobStatus';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { TASK_STATUS } from '../../../constants/serverConstants';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { PRIVATE_ROUTE } from '../../../routes/routes';

const ServerJobsDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, gpsId, jobId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();
  // const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [gps, setGps] = useState(null);
  const [task, setTask] = useState(null);
  const [log, setLog] = useState(null);
  const [logLoading, setLogLoading] = useState(true);

  const state = location.state;
  const modelId = state?.modelId;
  const mode = state?.file?.ext || 'txt';

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isEmpty(state)) {
        // Since the state is empty, there are two possibilities:
        // If the user tries to access the page directly, redirect to the projects page, otherwise go back
        const isHistoryStackEmpty = location.key === 'default';
        isHistoryStackEmpty ? navigate(PRIVATE_ROUTE.PROJECTS) : navigate(-1);
      }

      const [{ data: gpsRes }, { data: taskRes }] = await requestAll([
        serverApis.getServer(projectId, gpsId),
        serverApis.getIndexingTask(projectId, modelId, jobId),
      ]);
      setGps(gpsRes);
      setTask(taskRes.result);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refreshTasks = async () => {
    try {
      const { data: taskRes } = await serverApis.getIndexingTask(
        projectId,
        modelId,
        jobId
      );
      setTask(taskRes.result);
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        t('fetch', { ns: 'errors', content: t('job', { ns: 'gps' }) })
      );
      setTask(null);
      setError(true);
    }
  };

  const fetchLogs = async () => {
    try {
      const logRes = await serverApis.getTaskLog(
        projectId,
        task.instanceSetId,
        jobId
      );
      setLog(logRes.data);
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        t('fetch', {
          ns: 'errors',
          content: t('log', { ns: 'gps' }),
        })
      );
      setError(true);
    } finally {
      logLoading && setLogLoading(false);
    }
  };

  useEffect(() => {
    if (!task) return;
    const taskInterval = setInterval(refreshTasks, 10000);
    if (
      task.status !== TASK_STATUS.RUNNING &&
      task.status !== TASK_STATUS.PENDING
    ) {
      clearInterval(taskInterval);
    }

    return () => clearInterval(taskInterval);
  }, [task]);

  useEffect(() => {
    if (!task) return;
    fetchLogs();
  }, [task]);

  return (
    <Container>
      {loading ? (
        <Loading />
      ) : error ? (
        <AlertMessage
          severity="error"
          message={t('fetch', {
            ns: 'errors',
            content: t('log', { ns: 'gps' }),
          })}
        />
      ) : (
        <>
          <Breadcrumbs serverName={gps?.name} />
          <TitlePanel title={t('jobDetails', { ns: 'titles' })} />
          {logLoading ? (
            <Loading />
          ) : (
            log &&
            task && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    m: 1,
                  }}
                >
                  <Text text={t('status', { ns: 'gps' })} />:
                  <JobStatus status={task.status} />
                </Box>
                <Editor mode={mode} code={log} readOnly="nocursor" />
              </>
            )
          )}
        </>
      )}
    </Container>
  );
};

export default ServerJobsDetailsPage;
